import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Field, Modal } from '../components/ui';
import type { ResourceSummary } from '../types';

/**
 * Kalıcı giriş yolu.
 *
 * Motor hesaba kendi istemcisiyle giriyor, Instagram doğrulama kodu isterse burada sana
 * soruyor. Oluşan oturum onu kullanacak cihaza ait olduğu için uzun ömürlü — tarayıcıdan
 * aktarılan çerez o cihaza ait değil ve Instagram onu saatler içinde iptal ediyor.
 *
 * Kod burada hiçbir yere yazılmıyor: bir kez doğrulama yapıp değersizleşiyor, saklamak
 * yalnızca sorumluluk olurdu.
 */
interface Attempt {
  id: string;
  identity: string;
  state: 'starting' | 'running' | 'awaiting_code' | 'succeeded' | 'failed';
  codeTarget: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  elapsedSeconds: number;
}

const DURUM: Record<Attempt['state'], string> = {
  starting: 'Giriş başlatılıyor…',
  running: 'Instagram ile konuşuluyor…',
  awaiting_code: 'Instagram bir doğrulama kodu istedi',
  succeeded: 'Giriş tamamlandı, oturum kaydedildi.',
  failed: 'Giriş başarısız.',
};

export function NativeLogin({
  account,
  onClose,
  onDone,
}: {
  account: ResourceSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptRef = useRef<string | null>(null);

  const hata = (reason: unknown) =>
    setError(reason instanceof Error ? reason.message : String(reason));

  useEffect(() => {
    let iptal = false;
    api
      .post<Attempt>(`/v1/instagram/accounts/${account.id}/login`, {})
      .then((value) => {
        if (iptal) return;
        attemptRef.current = value.id;
        setAttempt(value);
      })
      .catch(hata);
    return () => {
      iptal = true;
    };
  }, [account.id]);

  // Operatör e-postasını okurken bekliyoruz; yoklama, kod istendiği anı görmenin tek yolu.
  useEffect(() => {
    if (!attempt || attempt.state === 'succeeded' || attempt.state === 'failed') return;
    const timer = setInterval(() => {
      const id = attemptRef.current;
      if (!id) return;
      api
        .get<Attempt>(`/v1/instagram/accounts/${account.id}/login/${id}`)
        .then((value) => {
          setAttempt(value);
          if (value.state === 'succeeded') onDone();
        })
        .catch(hata);
    }, 2000);
    return () => clearInterval(timer);
  }, [account.id, attempt, onDone]);

  const gonder = useCallback(() => {
    const id = attemptRef.current;
    if (!id || !code.trim()) return;
    setBusy(true);
    setError(null);
    api
      .post<Attempt>(`/v1/instagram/accounts/${account.id}/login/${id}/code`, { code: code.trim() })
      .then((value) => {
        setAttempt(value);
        setCode('');
      })
      .catch(hata)
      .finally(() => setBusy(false));
  }, [account.id, code]);

  const kodBekleniyor = attempt?.state === 'awaiting_code';

  return (
    <Modal
      title={`Giriş yap — ${account.name}`}
      submitLabel={kodBekleniyor ? 'Kodu gönder' : 'Kapat'}
      onClose={onClose}
      onSubmit={kodBekleniyor ? gonder : onClose}
      busy={busy || (!attempt && !error)}
      error={error}
    >
      <p className="note">
        {attempt ? DURUM[attempt.state] : 'Giriş başlatılıyor…'}
        {attempt?.state === 'failed' && attempt.errorMessage ? ` ${attempt.errorMessage}` : ''}
      </p>

      {kodBekleniyor && (
        <>
          <Field
            label="Doğrulama kodu"
            hint={
              attempt?.codeTarget === 'email'
                ? 'Instagram kodu hesabın e-posta adresine gönderdi.'
                : attempt?.codeTarget === 'sms'
                  ? 'Instagram kodu hesabın telefon numarasına gönderdi.'
                  : 'Instagram kodu hesabın kayıtlı iletişim adresine gönderdi.'
            }
          >
            <input
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <p className="note">
            Kod hiçbir yere kaydedilmiyor — bekleyen girişe iletilip unutuluyor. Bir kez
            doğrulama yaptıktan sonra zaten değersiz.
          </p>
        </>
      )}

      {attempt?.state === 'succeeded' && (
        <p className="note">
          Bu oturum motorun kendi cihazına ait olduğu için uzun ömürlü. Tarayıcıdan aktarılan
          çerezlerin aksine saatler içinde iptal edilmesi beklenmiyor.
        </p>
      )}
    </Modal>
  );
}
